<template>
  <div class="page-bulk-exports extra-large-page">
    <v-card
      v-if="retryData"
      dark
      color="bluegray"
      max-width="600"
      class="mx-auto"
    >
      <v-card-title>
        Your export is ready!
      </v-card-title>

      <v-card-subtitle>
        It should start downloading itself.
      </v-card-subtitle>

      <v-card-text>
        <div v-if="retryData.counts.pending">
          There were {{ retryData.counts.pending }} pending profiles.
        </div>
        <div v-if="retryData.counts.error">
          {{ retryData.counts.error }} profiles resulted in error.
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />

        <v-btn
          text
          @click="retryExport"
        >
          <v-icon left>
            refresh
          </v-icon>

          Retry
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-card
      max-width="600"
      class="mx-auto accent mt-sm-8 mt-md-12 mt-lg-16"
      flat
    >
      <div class="d-flex flex-wrap align-center justify-space-between pr-md-4">
        <div>
          <v-card-title>
            Bulk Export
          </v-card-title>

          <v-card-subtitle>
            Add upto {{ maxItems }} profiles at once.
          </v-card-subtitle>
        </div>

        <v-btn
          @click="$router.replace({ name: 'InfluencerInsight' })"
          class="shadow--primary ml-4 ml-md-0"
          color="primary"
          depressed
          large
        >
          <v-icon left>
            arrow_back
          </v-icon>

          BACK
        </v-btn>
      </div>

      <v-card-text>
        <!-- Show the error messages here -->
        <p
          v-if="items.length > maxItems"
          class="red--text"
        >
          Please enter a maximum of 200 profiles at once
        </p>

        <!-- Show error messages for all invalid profile/links -->
        <p
          v-for="(item, index) in invalidItems"
          :key="index"
          class="red--text"
        >
          Profile link #{{ items.indexOf(item) + 1 }} ({{ item }}) is not valid
        </p>

        <!-- Show the select inputs -->
        <div class="d-flex mt-3 mt-sm-0">
          <!-- show the input for searching terms -->
          <div class="search-input">
            <v-select
              solo
              flat
              hide-details
              v-model="selectedType"
              :items="typeOptions"
              placeholder="Export File Type"
            ></v-select>
          </div>
          <!-- the select options for different platforms -->
          <platform-selector
            :value="selectedPlatform"
            class="platform-selector"
            @input="handlePlatformChange"
          />
        </div>

        <!-- Show the textarea input -->
        <v-textarea
          solo
          flat
          hide-details
          class="mt-4"
          v-model="itemsInput"
          placeholder="Profile URLs/Usernames"
        ></v-textarea>
      </v-card-text>

      <v-card-actions class="px-4">
        <v-spacer></v-spacer>

        <v-btn
          :class="{ 'shadow--primary': !shouldCTABeDisabled }"
          :disabled="shouldCTABeDisabled"
          :loading="isLoading"
          color="primary"
          type="submit"
          large
          depressed
          @click="handleSubmit"
        >
          Export
        </v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
// Helper functions
import { getClientID } from "@/helpers/clientHelper"
import platformRegex from "@/helpers/platformRegex"
import isURL from "@/helpers/isURL"

// Selector Blocks
const PlatformSelector = () => import("@/blocks/common/selectors/PlatformSelector.vue")

export default {
  name: "BulkInsightsExport",

  components: {
    PlatformSelector
  },

  data: () => ({
    selectedType: null,
    selectedPlatform: "instagram",

    // Store the entire links input in this string
    itemsInput: "",

    minItems: 2,
    maxItems: 200,

    isLoading: false,
    triggerId: null,

    retryData: null
  }),

  computed: {
    /**
     * Compute the input string and try to generate an array
     *
     * @returns {Array}
     */
    items() {
      const breakCharacter = "*STRING&ENDS&HERE*"

      return this.itemsInput.replaceAll(" ", breakCharacter)
        .replaceAll("\n", breakCharacter)
        .split(breakCharacter)
        .map((item) => item.trim())
        .filter((item) => Boolean(item))
    },

    /**
     * Whether or not the items are all within range
     *
     * @returns {Boolean}
     */
    areItemsOverflowing() {
      return this.items.length >= this.maxItems
    },

    /**
     * Make sure that all the items are valid string values, otherwise return an array of line numbers which are invalid
     *
     * @returns {Array}
     */
    invalidItems() {
      return this.items.filter((item) => {
        // Check if the input is kind of an URL
        if (isURL(item)) {
          // Try getting the regex match for it
          const match = item.match(platformRegex[this.selectedPlatform])

          // If the match is found, hence found the userID for this link
          if (match !== null && match[1]) {
            return false
          }
          else {
            return true
          }
        }
        // Fallback to true
        else {
          return false
        }
      })
    },

    /**
     * Whether or not the submit button be disabled
     *
     * @returns {Boolean}
     */
    shouldCTABeDisabled() {
      return (this.items.length < this.minItems) || this.isLoading || this.invalidItems.length > 0
    },

    /**
     * Select input possible option values for type
     *
     * @returns {Array}
     */
    typeOptions() {
      const items = [
        {
          text: "PDF",
          value: "zip",
          condition: this.constants.model.user.allowedServices.actionBulkExportInfluencerInsightsPDF
        },

        {
          text: "XLSX",
          value: "sheet",
          condition: this.constants.model.user.allowedServices.actionBulkExportInfluencerInsightsXLSX
        }
      ]

      return items.filter((item) => this.$store.getters["auth/isServiceAllowed"](item.condition))
    }
  },

  methods: {
    handlePlatformChange(value) {
      this.selectedPlatform = value
    },

    async handleSubmit() {
      // If some request is in queue
      if (this.isLoading) {
        this.$store.dispatch("toasts/add", {
          text: "Please wait ..."
        })

        return false
      }

      // Check if the there are at least 2 items
      else if (this.items.length < this.minItems) {
        this.$store.dispatch("toasts/add", {
          text: `Please add at least ${this.minItems} profiles`
        })

        return false
      }

      else if (this.selectedType === null) {
        this.$store.dispatch("toasts/add", {
          text: "Please select an export file type"
        })

        return false
      }

      // Otherwise, make the request
      else {
        // Put a loader
        this.isLoading = true
        this.triggerId = String(Date.now())

        // Hide the previous dialog
        this.retryData = null

        try {
          await axios({
            url: "/api/export/influencers/insights/" + this.selectedPlatform,
            method: "POST",
            data: {
              type: this.selectedType,
              // Pass the current tab's ID to make sure only we download the file once
              clientId: getClientID(),
              triggerId: this.triggerId,
              accounts: this.items.map((item) => {
                // Check if the input is kind of an URL
                if (isURL(item)) {
                  // Try getting the regex match for it
                  const match = item.match(platformRegex[this.selectedPlatform])

                  // If the match is found, hence found the userID for this link
                  if (match !== null && match[1]) {
                    return match[1]
                  }
                  else {
                    return item
                  }
                }
                // Fallback to true
                else {
                  return item
                }
              })
            }
          })

          // Show an alert message
          this.$store.dispatch("toasts/add", {
            text: "Your export is in queue, it might take some minutes"
          })

          // Reset the form
          this.itemsInput = ""
        } catch (error) {
          logger({ type: "Bulk Influencer Insights Export Error", error })

          this.$store.dispatch("toasts/add", {
            text: error.response.data?.error?.message || "An error occurred. Please refresh."
          })
        } finally {
          this.isLoading = false
        }
      }
    },

    /**
     * Requeue the export with retryData received from web-sockets
     */
    async retryExport() {
      // Put a loader
      this.isLoading = true
      this.triggerId = String(Date.now())

      try {
        // Get the platform and accounts value from retryData
        const platform = Object.keys(this.retryData.input)[0]
        const accounts = this.retryData.input[platform]

        await axios({
          url: "/api/export/influencers/insights/" + platform,
          method: "POST",
          data: {
            accounts,
            type: this.retryData.type,
            // Pass the current tab's ID to make sure only we download the file once
            clientId: getClientID(),
            triggerId: this.triggerId,
            modelId: this.retryData.modelId
          }
        })

        // Show an alert message
        this.$store.dispatch("toasts/add", {
          text: "Your export is in queue, it might take some minutes"
        })

        // Reset the form
        this.itemsInput = ""
        this.retryData = null
      } catch (error) {
        logger({ type: "Bulk Influencer Insights Export Error", error })

        this.$store.dispatch("toasts/add", {
          text: error.response.data?.error?.message || "An error occurred. Please refresh."
        })
      } finally {
        this.isLoading = false
      }
    },

    /**
     * When the web-socket connection says the download file is ready
     *
     * @param {Object} event
     * @returns {void}
     */
    handleDownloadReady(event) {
      if (event.detail.data.misc.for === "bulk") {
        this.$store.dispatch("toasts/add", {
          text: "Your bulk export is ready and being downloaded!"
        })
      }
    },

    /**
     * Handle the event and show option to retry the export
     *
     * @param {Object} event
     * @returns {void}
     */
    handleBulkExportResult(event) {
      this.retryData = event.detail.data.localData
    }
  },

  /**
   * When the Options API mounted lifecycle hook is called
   *
   * @returns {void}
   */
  mounted() {
    if (this.typeOptions.length === 1) {
      this.selectedType = this.typeOptions[0].value
    }

    // Keep listening for an event
    window.addEventListener("ReadyToDownload", this.handleDownloadReady)
    window.addEventListener("BulkExportResult", this.handleBulkExportResult)
  },

  /**
   * Before this component is about to be removed
   *
   * @returns {void}
  */
  beforeDestroy() {
    // De-register the event listener
    window.removeEventListener("ReadyToDownload", this.handleDownloadReady)
    window.removeEventListener("BulkExportResult", this.handleBulkExportResult)
  }
}
</script>

<style lang="stylus">
// to contain the entire box structure in large viewport
.page-bulk-exports
  overflow-x auto

  .options-container
    max-width 1120px
    margin 0 auto

  .results-container
    max-width 1320px
    margin 0 auto

  .search-input
    width 100%

    .v-input__slot
      height 48px
      border-top-right-radius 0
      border-bottom-right-radius 0

    .v-label
      padding-top 4px

  .platform-selector
    .v-input__slot
      border-top-left-radius 0
      border-bottom-left-radius 0
</style>
